import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import SearchIcon from "@mui/icons-material/Search";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  FINANCEIRO_ACCENT,
  financeiroToolbarSx,
} from "../../components/financeiro-lancamentos.theme";
import type { ContasReceberFiltros } from "../types/contas-receber.types";
import {
  periodoVencimentoParaDatas,
  type ContasReceberPeriodoVencimentoPreset,
} from "../helpers/contas-receber-datas";

type OpcaoStr = { value: string; label: string };

const OPCOES_STATUS: OpcaoStr[] = [
  { value: "", label: "Todos" },
  { value: "PENDENTE", label: "Pendente" },
  { value: "RECEBIDO", label: "Pago" },
  { value: "VENCIDO", label: "Vencido" },
  { value: "CANCELADO", label: "Cancelado" },
  { value: "ESTORNADO", label: "Estornado" },
];

const OPCOES_ORIGEM: OpcaoStr[] = [
  { value: "", label: "Todas" },
  { value: "VENDA_LOJA", label: "Venda loja" },
  { value: "INSCRICAO_EVENTO", label: "Inscrição evento" },
  { value: "PATROCINIO", label: "Patrocínio" },
  { value: "AJUSTE_MANUAL", label: "Ajuste manual" },
  { value: "ESTORNO_REVERSAO", label: "Estorno/reversão" },
];

const OPCOES_PERIODO: { value: ContasReceberPeriodoVencimentoPreset; label: string }[] = [
  { value: "hoje", label: "Hoje" },
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
  { value: "mes", label: "Este mês" },
];

const toggleSx = {
  flexWrap: "wrap" as const,
  "& .MuiToggleButton-root": {
    px: 1.25,
    py: 0.75,
    textTransform: "none" as const,
    fontSize: "0.8125rem",
    "&.Mui-selected": {
      bgcolor: "rgba(249, 96, 48, 0.1)",
      borderColor: FINANCEIRO_ACCENT,
      color: FINANCEIRO_ACCENT,
      fontWeight: 600,
    },
  },
};

const fieldSx = { bgcolor: "background.paper", borderRadius: 1.5 };

export type ContasReceberFiltrosProps = {
  filtros: ContasReceberFiltros;
  setFiltros: (
    patch:
      | Partial<ContasReceberFiltros>
      | ((p: ContasReceberFiltros) => ContasReceberFiltros),
  ) => void;
  onAplicar: () => void;
  onLimpar: () => void;
  loading?: boolean;
  resetPeriodoPresetTick?: number;
};

export default function ContasReceberFiltros({
  filtros,
  setFiltros,
  onAplicar,
  onLimpar,
  loading,
  resetPeriodoPresetTick = 0,
}: ContasReceberFiltrosProps) {
  const [periodoPreset, setPeriodoPreset] =
    useState<ContasReceberPeriodoVencimentoPreset | "personalizado">("mes");

  useEffect(() => {
    setPeriodoPreset("mes");
  }, [resetPeriodoPresetTick]);

  const aplicarPeriodo = (preset: ContasReceberPeriodoVencimentoPreset) => {
    setPeriodoPreset(preset);
    const datas = periodoVencimentoParaDatas(preset);
    if (datas) {
      setFiltros((prev) => ({
        ...prev,
        vencimentoInicio: datas.vencimentoInicio,
        vencimentoFim: datas.vencimentoFim,
      }));
    }
  };

  const patch =
    (field: keyof ContasReceberFiltros) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFiltros((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const periodoToggleValue =
    periodoPreset === "personalizado" ? "" : periodoPreset;

  return (
    <MDBox sx={financeiroToolbarSx}>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterListOutlinedIcon sx={{ color: FINANCEIRO_ACCENT }} />
          <MDTypography variant="button" fontWeight="medium" color="text">
            Filtros operacionais
          </MDTypography>
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Status
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filtros.status ?? ""}
            onChange={(_, next: string | null) => {
              if (next === null) return;
              setFiltros((prev) => ({ ...prev, status: next }));
            }}
            sx={toggleSx}
          >
            {OPCOES_STATUS.map((opt) => (
              <ToggleButton
                key={opt.value || "all-status"}
                value={opt.value}
                disabled={Boolean(loading)}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Vencimento
          </Typography>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", lg: "flex-end" }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={periodoToggleValue}
              onChange={(_, next: ContasReceberPeriodoVencimentoPreset | null) => {
                if (next) aplicarPeriodo(next);
              }}
              sx={{ ...toggleSx, flexShrink: 0 }}
            >
              {OPCOES_PERIODO.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value} disabled={Boolean(loading)}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 160, flex: 1 }}>
              <InputLabel shrink htmlFor="cr-venc-inicio">
                Vencimento início
              </InputLabel>
              <OutlinedInput
                id="cr-venc-inicio"
                type="date"
                value={filtros.vencimentoInicio ?? ""}
                onChange={(e) => {
                  setPeriodoPreset("personalizado");
                  patch("vencimentoInicio")(e);
                }}
                label="Vencimento início"
                notched
                disabled={Boolean(loading)}
                sx={fieldSx}
              />
            </FormControl>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 160, flex: 1 }}>
              <InputLabel shrink htmlFor="cr-venc-fim">
                Vencimento fim
              </InputLabel>
              <OutlinedInput
                id="cr-venc-fim"
                type="date"
                value={filtros.vencimentoFim ?? ""}
                onChange={(e) => {
                  setPeriodoPreset("personalizado");
                  patch("vencimentoFim")(e);
                }}
                label="Vencimento fim"
                notched
                disabled={Boolean(loading)}
                sx={fieldSx}
              />
            </FormControl>
          </Stack>
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Origem
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filtros.origem ?? ""}
            onChange={(_, next: string | null) => {
              if (next === null) return;
              setFiltros((prev) => ({ ...prev, origem: next }));
            }}
            sx={toggleSx}
          >
            {OPCOES_ORIGEM.map((opt) => (
              <ToggleButton
                key={opt.value || "all-origem"}
                value={opt.value}
                disabled={Boolean(loading)}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel shrink htmlFor="cr-busca">
            Busca
          </InputLabel>
          <OutlinedInput
            id="cr-busca"
            value={filtros.busca ?? ""}
            onChange={patch("busca")}
            label="Busca"
            placeholder="Cliente, descrição, documento…"
            notched
            disabled={Boolean(loading)}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            }
            sx={fieldSx}
          />
        </FormControl>

        <Divider />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={Boolean(filtros.apenasVencidos)}
                onChange={(_, checked) =>
                  setFiltros((prev) => ({ ...prev, apenasVencidos: checked }))
                }
                disabled={Boolean(loading)}
              />
            }
            label={<Typography variant="body2">Apenas vencidos</Typography>}
            sx={{ m: 0 }}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              disabled={Boolean(loading)}
              onClick={onAplicar}
              sx={{
                background: `linear-gradient(195deg, ${FINANCEIRO_ACCENT}, #e04a1a)`,
              }}
            >
              Aplicar filtros
            </MDButton>
            <MDButton
              color="dark"
              variant="outlined"
              size="small"
              disabled={Boolean(loading)}
              onClick={onLimpar}
            >
              Limpar
            </MDButton>
          </Stack>
        </Stack>
      </Stack>
    </MDBox>
  );
}
